// @ts-nocheck
/**
 * CCMS Auth — documented showcase / stub page
 *
 * Documents the SDK-bound auth gating components shipped under
 * `@/components/ccms-components/auth`:
 *   - PermissionGate  (conditional render by role/permission)
 *   - ProtectedRoute  (auth-required route wrapper)
 *   - RoleGuard       (conditional render by role)
 *
 * IMPORTANT — why nothing here is rendered live:
 * Every one of these components calls `usePermissions()` on mount, which in
 * turn calls `useEnrichedAuth()` → the SDK's `useAuth()` from
 * `@actschurch/ccms-sdk`. That SDK hook THROWS
 *   "useAuth must be used within an AuthProvider"
 * when no <AuthProvider> wraps the tree. The Demo shell does not provide a
 * logged-in CCMS SDK auth context, so rendering any of these components here
 * would crash the page. They are therefore documented as code-only stubs.
 *
 * Importing the components (below) is safe — only *rendering* them invokes the
 * hook. We import them so this page references the real, exported symbols and
 * stays honest about what ships.
 */

import {
  Alert,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@/components/ui';

// Real exported symbols (import-safe; not rendered — see header note).
import {
  PermissionGate,
  ProtectedRoute,
  RoleGuard,
} from '@/components/ccms-components/auth';

// Reference the imports so linters/bundlers keep them and so this page is
// provably wired to the actual exports — without ever mounting them.
const REGISTRY = { PermissionGate, ProtectedRoute, RoleGuard } as const;

const NEEDS_SDK_AUTH = 'CCMS SDK auth context (logged-in user + permissions)';

/** One "prop row": name — type — required? */
function PropRow({
  name,
  type,
  required,
  note,
}: {
  name: string;
  type: string;
  required?: boolean;
  note?: string;
}) {
  return (
    <Text size="sm">
      <Code>{name}</Code>{' '}
      <Text span c="dimmed">
        — {type} —{' '}
      </Text>
      <Text span c={required ? 'red' : 'dimmed'} fw={required ? 600 : 400}>
        {required ? 'required' : 'optional'}
      </Text>
      {note ? (
        <Text span c="dimmed">
          {' '}
          · {note}
        </Text>
      ) : null}
    </Text>
  );
}

export default function CcmsAuthPage() {
  return (
    <Stack gap="xl">
      {/* ---------------------------------------------------------------- */}
      {/* Header                                                            */}
      {/* ---------------------------------------------------------------- */}
      <div>
        <Title order={2}>
          CCMS Auth (PermissionGate, ProtectedRoute, RoleGuard)
        </Title>
        <Text c="dimmed" size="sm" mt={4}>
          Role &amp; permission gating components from{' '}
          <Code>@/components/ccms-components/auth</Code>. Each is documented
          below with its real props and a usage snippet. They are{' '}
          <Text span fw={600}>
            not rendered live
          </Text>{' '}
          on this page because they read the logged-in user via the CCMS SDK
          auth context.
        </Text>
      </div>

      <Alert
        alertVariant="warning"
        title="All components on this page are documentation-only"
      >
        <Text size="sm">
          <Code>PermissionGate</Code>, <Code>ProtectedRoute</Code> and{' '}
          <Code>RoleGuard</Code> all call <Code>usePermissions()</Code> on
          mount, which calls the SDK&apos;s <Code>useAuth()</Code>. Without an{' '}
          <Code>&lt;AuthProvider&gt;</Code> that hook throws{' '}
          <Code>“useAuth must be used within an AuthProvider”</Code>. The Demo
          shell has no logged-in SDK session, so these are shown as code stubs
          only — importing them is safe, rendering them is not.
        </Text>
      </Alert>

      {/* ---------------------------------------------------------------- */}
      {/* PermissionGate                                                    */}
      {/* ---------------------------------------------------------------- */}
      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="xs">
                <ThemeIcon variant="light" color="grape" size="lg" radius="md">
                  PG
                </ThemeIcon>
                <Title order={4}>PermissionGate</Title>
              </Group>
              <Text size="sm" c="dimmed" mt={4}>
                Conditionally renders <Code>children</Code> based on the current
                user&apos;s roles and/or permissions. When access is denied it
                can <Code>hide</Code>, <Code>disable</Code>, or <Code>blur</Code>{' '}
                the content (or show a <Code>fallback</Code>).
              </Text>
            </div>
            <Badge color="grape" variant="light">
              doc-only
            </Badge>
          </Group>

          <Divider label="Key props" labelPosition="left" />
          <Stack gap={4}>
            <PropRow name="children" type="ReactNode" required />
            <PropRow
              name="roles"
              type="string[]"
              note="role names to check"
            />
            <PropRow
              name="permissions"
              type="string[]"
              note="permission keys to check"
            />
            <PropRow
              name="requireAll"
              type="boolean"
              note="default false → ANY of roles/permissions"
            />
            <PropRow
              name="mode"
              type="'hide' | 'disable' | 'blur'"
              note="default 'hide'"
            />
            <PropRow name="fallback" type="ReactNode" note="shown when denied" />
          </Stack>

          <Divider label="Usage" labelPosition="left" />
          <Code block>{`import { PermissionGate } from '@/components/ccms-components/auth';

// Hide an admin panel unless the user has one of these roles
<PermissionGate roles={['admin', 'staff']} fallback={<div>Access Denied</div>}>
  <AdminPanel />
</PermissionGate>

// Disable (grey out) a button unless the user can edit users
<PermissionGate permissions={['edit:users']} mode="disable">
  <Button>Edit User</Button>
</PermissionGate>`}</Code>

          <Alert alertVariant="info" title="Needs to run live">
            <Text size="sm">{NEEDS_SDK_AUTH}</Text>
          </Alert>
        </Stack>
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* ProtectedRoute                                                    */}
      {/* ---------------------------------------------------------------- */}
      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="xs">
                <ThemeIcon variant="light" color="blue" size="lg" radius="md">
                  PR
                </ThemeIcon>
                <Title order={4}>ProtectedRoute</Title>
              </Group>
              <Text size="sm" c="dimmed" mt={4}>
                Route wrapper that requires authentication. While the SDK
                resolves auth it renders a loading state; if the user is not
                authenticated it can redirect (via{' '}
                <Code>window.location.href = redirectTo</Code>) and/or render a{' '}
                <Code>fallback</Code>. Optionally enforces roles/permissions.
              </Text>
            </div>
            <Badge color="blue" variant="light">
              doc-only
            </Badge>
          </Group>

          <Divider label="Key props" labelPosition="left" />
          <Stack gap={4}>
            <PropRow name="children" type="ReactNode" required />
            <PropRow
              name="requireAuth"
              type="boolean"
              note="default true"
            />
            <PropRow
              name="requiredRoles"
              type="string[]"
              note="user needs at least one"
            />
            <PropRow
              name="requiredPermissions"
              type="string[]"
              note="user needs at least one"
            />
            <PropRow
              name="redirectTo"
              type="string"
              note="default '/login'"
            />
            <PropRow
              name="fallback"
              type="ReactNode"
              note="default <div>Access Denied</div>"
            />
          </Stack>

          <Divider label="Usage" labelPosition="left" />
          <Code block>{`import { ProtectedRoute } from '@/components/ccms-components/auth';

<ProtectedRoute
  requireAuth
  requiredRoles={['admin']}
  redirectTo="/login"
  fallback={<div>Loading…</div>}
>
  <AdminDashboard />
</ProtectedRoute>`}</Code>

          <Alert alertVariant="danger" title="Do not render in the Demo shell">
            <Text size="sm">
              In addition to {NEEDS_SDK_AUTH.toLowerCase()},{' '}
              <Code>ProtectedRoute</Code> performs real navigation
              (<Code>window.location.href</Code>) when unauthenticated. Wrap it
              only around actual app routes inside an authenticated{' '}
              <Code>&lt;AuthProvider&gt;</Code> tree — never around Demo content.
            </Text>
          </Alert>
        </Stack>
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* RoleGuard                                                         */}
      {/* ---------------------------------------------------------------- */}
      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="xs">
                <ThemeIcon variant="light" color="teal" size="lg" radius="md">
                  RG
                </ThemeIcon>
                <Title order={4}>RoleGuard</Title>
              </Group>
              <Text size="sm" c="dimmed" mt={4}>
                Lightweight role-only gate. Renders <Code>children</Code> when
                the user holds the required role(s); otherwise renders{' '}
                <Code>fallback</Code> (or nothing). Accepts a single role string
                or an array.
              </Text>
            </div>
            <Badge color="teal" variant="light">
              doc-only
            </Badge>
          </Group>

          <Divider label="Key props" labelPosition="left" />
          <Stack gap={4}>
            <PropRow name="children" type="ReactNode" required />
            <PropRow
              name="roles"
              type="string | string[]"
              required
              note="required role(s)"
            />
            <PropRow
              name="requireAll"
              type="boolean"
              note="default false → ANY role matches"
            />
            <PropRow name="fallback" type="ReactNode" note="shown when denied" />
          </Stack>

          <Divider label="Usage" labelPosition="left" />
          <Code block>{`import { RoleGuard } from '@/components/ccms-components/auth';

// Single role
<RoleGuard roles="admin">
  <AdminPanel />
</RoleGuard>

// Any of several roles, with a fallback
<RoleGuard roles={['admin', 'staff']} requireAll={false} fallback={<div>No Access</div>}>
  <StaffTools />
</RoleGuard>`}</Code>

          <Alert alertVariant="info" title="Needs to run live">
            <Text size="sm">{NEEDS_SDK_AUTH}</Text>
          </Alert>
        </Stack>
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* Footer / how to demo for real                                     */}
      {/* ---------------------------------------------------------------- */}
      <Card withBorder padding="lg" radius="md" bg="var(--mantine-color-default-hover)">
        <Stack gap="xs">
          <Title order={5}>Seeing these in action</Title>
          <Text size="sm" c="dimmed">
            To exercise these components live, mount them inside a tree wrapped
            by the CCMS SDK <Code>&lt;AuthProvider&gt;</Code> with a logged-in
            user. The gate result is driven by{' '}
            <Code>useEnrichedAuth()</Code> → <Code>user.roles</Code> /{' '}
            <Code>user.permissions</Code>:
          </Text>
          <Code block>{`<AuthProvider>
  {/* user is logged in here, so the hooks resolve instead of throwing */}
  <RoleGuard roles="admin">
    <AdminOnlyWidget />
  </RoleGuard>
</AuthProvider>`}</Code>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm" mt="xs">
            {Object.keys(REGISTRY).map((name) => (
              <Group key={name} gap="xs">
                <ThemeIcon variant="light" size="sm" radius="sm" color="gray">
                  ✓
                </ThemeIcon>
                <Text size="sm">
                  <Code>{name}</Code> exported &amp; documented
                </Text>
              </Group>
            ))}
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
